import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

const styles = {
  header: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    height: "var(--header-height)",
    backgroundColor: "rgba(15, 13, 10, 0.9)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid rgba(196, 162, 101, 0.15)",
    zIndex: 1000,
  },
  headerInner: {
    maxWidth: "var(--max-width)",
    margin: "0 auto",
    padding: "0 var(--space-6)",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
  },
  logoImg: {
    height: "40px",
    width: "auto",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-8)",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-6)",
  },
  navLink: {
    fontSize: "var(--text-sm)",
    fontWeight: 500,
    color: "var(--color-gray-600)",
    textDecoration: "none",
    transition: "color var(--transition-fast)",
  },
  navLinkActive: {
    color: "var(--color-primary)",
  },
  navActions: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
  },
  loginBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "var(--space-2) var(--space-4)",
    fontSize: "var(--text-sm)",
    fontWeight: 500,
    color: "#EDE8E0",
    backgroundColor: "transparent",
    border: "1px solid rgba(196, 162, 101, 0.3)",
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all var(--transition-fast)",
  },
  signUpBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "var(--space-2) var(--space-4)",
    fontSize: "var(--text-sm)",
    fontWeight: 600,
    color: "#0F0D0A",
    backgroundColor: "var(--color-primary)",
    border: "1px solid var(--color-primary)",
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all var(--transition-fast)",
  },
  main: {
    marginTop: "var(--header-height)",
    minHeight: "calc(100vh - var(--header-height) - 200px)",
  },
  footer: {
    backgroundColor: "#0A0908",
    color: "#A8A09A",
    padding: "var(--space-12) 0",
    borderTop: "1px solid rgba(196, 162, 101, 0.1)",
  },
  footerInner: {
    maxWidth: "var(--max-width)",
    margin: "0 auto",
    padding: "0 var(--space-6)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "var(--space-6)",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "var(--space-12)",
    width: "100%",
    textAlign: "center" as const,
  },
  footerHeading: {
    fontSize: "var(--text-sm)",
    fontWeight: 600,
    color: "#C4A265",
    marginBottom: "var(--space-3)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
  },
  footerLink: {
    display: "block",
    fontSize: "var(--text-sm)",
    color: "var(--color-gray-400)",
    textDecoration: "none",
    marginBottom: "var(--space-2)",
    transition: "color var(--transition-fast)",
  },
  footerDivider: {
    width: "100%",
    height: 1,
    backgroundColor: "rgba(196, 162, 101, 0.15)",
  },
  footerCopyright: {
    fontSize: "var(--text-sm)",
    color: "var(--color-gray-500)",
  },
};

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Pricing", path: "/pricing" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Contact", path: "/contact" },
];

export function PublicLayout() {
  const location = useLocation();

  return (
    <>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Link to="/" style={styles.logo}>
            <img src="/rslogo.png" alt="Reel Suite Pro" style={styles.logoImg} />
          </Link>

          <nav style={styles.nav}>
            <div style={styles.navLinks}>
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    ...styles.navLink,
                    ...(location.pathname === item.path
                      ? styles.navLinkActive
                      : {}),
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div style={styles.navActions}>
              <Link to="/login" style={styles.loginBtn}>
                Log In
              </Link>
              <Link to="/signup" style={styles.signUpBtn}>
                Sign Up Free
              </Link>
            </div>
          </nav>
        </div>
      </header>

      <main style={styles.main}>
        <Outlet />
      </main>

      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerGrid}>
            <div>
              <h4 style={styles.footerHeading}>Product</h4>
              <Link to="/pricing" style={styles.footerLink}>Pricing</Link>
              <Link to="/how-it-works" style={styles.footerLink}>How It Works</Link>
              <Link to="/signup" style={styles.footerLink}>Get Started</Link>
            </div>
            <div>
              <h4 style={styles.footerHeading}>Company</h4>
              <Link to="/contact" style={styles.footerLink}>Contact Us</Link>
              <a href="#" style={styles.footerLink}>About</a>
              <a href="#" style={styles.footerLink}>Blog</a>
            </div>
            <div>
              <h4 style={styles.footerHeading}>Legal</h4>
              <a href="#" style={styles.footerLink}>Privacy Policy</a>
              <a href="#" style={styles.footerLink}>Terms of Service</a>
              <a href="#" style={styles.footerLink}>Cookie Policy</a>
            </div>
          </div>

          <div style={styles.footerDivider} />

          <p style={styles.footerCopyright}>
            &copy; {new Date().getFullYear()} Reel Suite Pro. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}
